import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import API_BASE_URL from '../config/api';
import './AmenityForm.css';

const AmenityForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [loading, setLoading] = useState(isEdit);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    icon: '',
    type: 'Hotel',
    isActive: true,
    displayOrder: 0
  });

  const types = [
    { value: 'Hotel', label: 'Hotel' },
    { value: 'SPA', label: 'SPA' }
  ];

  useEffect(() => {
    if (isEdit) {
      fetchAmenity();
    }
  }, [id]);

  const fetchAmenity = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/amenities/${id}`);
      const amenity = response.data.data;
      setFormData({
        name: amenity.name || '',
        icon: amenity.icon || '',
        type: amenity.type || 'Hotel',
        isActive: amenity.isActive !== undefined ? amenity.isActive : true,
        displayOrder: amenity.displayOrder || 0
      });
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch amenity');
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!formData.name.trim()) {
      setError('Amenity name is required');
      return;
    }
    if (!formData.type) {
      setError('Amenity type must be Hotel or SPA');
      return;
    }
    try {
      if (isEdit) {
        await axios.put(`${API_BASE_URL}/admin/amenities/${id}`, formData);
      } else {
        await axios.post(`${API_BASE_URL}/admin/amenities`, formData);
      }
      navigate('/sy-admin/amenities');
    } catch (err) {
      setError(err.response?.data?.message || `Failed to ${isEdit ? 'update' : 'create'} amenity`);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="amenity-form-container">
          <div className="skeleton skeleton-text" style={{ width: '100%', height: '400px', borderRadius: '12px' }}></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="amenity-form-container">
        <div className="amenity-form-header">
          <h1>{isEdit ? 'Edit Amenity' : 'Add New Amenity'}</h1>
          <button onClick={() => navigate('/sy-admin/amenities')} className="btn btn-secondary">
            ← Back to Amenities
          </button>
        </div>

        {error && (
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            {error}
          </div>
        )}

        <div className="amenity-form-card">
          <form onSubmit={handleSubmit}>
            <div className="form-section">
              <h2>Basic Information</h2>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Amenity Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="e.g., Free WiFi, Towels"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="icon">Icon (Emoji/Text)</label>
                  <input
                    type="text"
                    id="icon"
                    name="icon"
                    value={formData.icon}
                    onChange={handleChange}
                    placeholder="e.g., 📶 or WiFi"
                    maxLength={10}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="type">Type *</label>
                  <select
                    id="type"
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    required
                  >
                    {types.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                  <small>Hotel: for room types. SPA: for spa services.</small>
                </div>
                <div className="form-group">
                  <label htmlFor="displayOrder">Display Order</label>
                  <input
                    type="number"
                    id="displayOrder"
                    name="displayOrder"
                    value={formData.displayOrder}
                    onChange={handleChange}
                    min="0"
                  />
                  <small>Lower numbers appear first</small>
                </div>
              </div>
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleChange}
                  />
                  <span>Active</span>
                </label>
                <small>Inactive amenities won't appear in room type / spa service selection</small>
              </div>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                {isEdit ? 'Update Amenity' : 'Create Amenity'}
              </button>
              <button type="button" onClick={() => navigate('/sy-admin/amenities')} className="btn btn-secondary">
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default AmenityForm;
